import { useState } from "react";
import { Modal, Tooltip } from "@mui/material";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

export default function SectionHelp({
  title,
  intro,
  options = [],
  footer,
  tooltip = "Learn about this section",
}) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);

  return (
    <>
      <Tooltip title={tooltip}>
        <HelpButton
          type="button"
          onClick={() => setOpen(true)}
          aria-label={tooltip}
        >
          <HelpOutlineRoundedIcon />
        </HelpButton>
      </Tooltip>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="section-help-title"
      >
        <ModalPosition>
          <HelpCard>
            <HelpHeader>
              <HeaderIcon>
                <HelpOutlineRoundedIcon />
              </HeaderIcon>
              <HeaderText>
                <h2 id="section-help-title">{title}</h2>
                {intro && <p>{intro}</p>}
              </HeaderText>
              <CloseButton
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t("closeHelp")}
              >
                <CloseRoundedIcon />
              </CloseButton>
            </HelpHeader>
            <OptionsGrid $single={options.length === 1}>
              {options.map((option) => (
                <OptionCard key={option.title}>
                  <h3>{option.title}</h3>
                  <p>{option.description}</p>
                  {option.bestFor && (
                    <BestFor>
                      <strong>{t("bestFor")}:</strong> {option.bestFor}
                    </BestFor>
                  )}
                  {option.features?.length > 0 && (
                    <FeatureList>
                      {option.features.map((feature) => (
                        <li key={feature}>{feature}</li>
                      ))}
                    </FeatureList>
                  )}
                </OptionCard>
              ))}
            </OptionsGrid>
            {footer && <HelpFooter>{footer}</HelpFooter>}
          </HelpCard>
        </ModalPosition>
      </Modal>
    </>
  );
}

const HelpButton = styled.button`
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  display: grid;
  place-items: center;
  padding: 0;
  border: 1px solid #dce3e5;
  border-radius: 10px;
  background: #ffffff;
  color: #667277;
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease;
  svg {
    font-size: 21px;
  }
  &:hover {
    border-color: rgba(0, 180, 204, 0.45);
    background: rgba(0, 180, 204, 0.08);
    color: var(--themeColor);
  }
`;

const ModalPosition = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
`;

const HelpCard = styled.section`
  width: min(720px, 100%);
  max-height: min(760px, 90vh);
  overflow-y: auto;
  padding: 22px;
  border: 1px solid #e0e6e8;
  border-radius: 20px;
  background: #ffffff;
  color: var(--lightThemeFontColor);
  box-shadow: 0 24px 70px rgba(16, 34, 39, 0.22);
  outline: none;
  @media (max-width: 560px) {
    padding: 17px;
    border-radius: 16px;
  }
`;

const HelpHeader = styled.header`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding-bottom: 18px;
`;

const HeaderIcon = styled.span`
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  display: grid;
  place-items: center;
  border-radius: 12px;
  background: rgba(0, 180, 204, 0.11);
  color: var(--themeColor);
  svg {
    font-size: 23px;
  }
`;

const HeaderText = styled.div`
  min-width: 0;
  flex: 1;
  h2 {
    margin: 0;
    color: #263337;
    font-size: 20px;
    line-height: 1.25;
  }
  p {
    margin: 6px 0 0;
    color: #687579;
    font-size: 13px;
    line-height: 1.5;
  }
`;

const CloseButton = styled.button`
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  display: grid;
  place-items: center;
  padding: 0;
  border: 0;
  border-radius: 9px;
  background: #f0f3f4;
  color: #687579;
  cursor: pointer;
  &:hover {
    background: #e5eaec;
    color: #29363a;
  }
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ $single }) => ($single ? "1fr" : "repeat(2, minmax(0, 1fr))")};
  gap: 12px;
  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const OptionCard = styled.article`
  padding: 17px;
  border: 1px solid #e1e7e9;
  border-radius: 15px;
  background: #f8fafa;
  h3 {
    margin: 0 0 7px;
    color: #2b383c;
    font-size: 15px;
  }
  > p {
    margin: 0;
    color: #647175;
    font-size: 12px;
    line-height: 1.55;
  }
`;

const BestFor = styled.p`
  margin: 12px 0 0 !important;
  padding-top: 11px;
  border-top: 1px solid #e3e9eb;
  color: #536166 !important;
  strong {
    color: var(--themeColor);
  }
`;

const FeatureList = styled.ul`
  margin: 12px 0 0;
  padding: 0;
  display: grid;
  gap: 7px;
  list-style: none;
  li {
    position: relative;
    padding-left: 15px;
    color: #526065;
    font-size: 11px;
    line-height: 1.4;
  }
  li::before {
    content: "";
    position: absolute;
    left: 0;
    top: 6px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--themeColor);
  }
`;

const HelpFooter = styled.p`
  margin: 14px 0 0;
  padding: 13px 14px;
  border-radius: 11px;
  background: rgba(0, 180, 204, 0.08);
  color: #526166;
  font-size: 12px;
  line-height: 1.5;
`;
